/**
 * Base Service
 * Provides common functionality for all domain services
 */
import type { SupabaseClient } from '@supabase/supabase-js';

import { getLogger } from '@kit/logger';
import type { Logger } from '@kit/logger';
import {
  captureServerActionError,
  captureServiceError,
} from '@kit/shared/lib/sentry-helpers';
import { Result, ServiceError } from '@kit/shared/result';
import type { Database } from '@kit/supabase/database';

import type { AuditAction, AuditResourceType } from '../audit';
import {
  extractCheckConstraintFromError,
  extractDuplicateFieldFromError,
} from './base.service.helpers';
import {
  extractDeniedObjectName,
  isAlwaysBugDbError,
} from './db-error-classification';

export abstract class BaseService<TDB = Database> {
  protected logger: Logger | null = null;

  constructor(protected client: SupabaseClient<TDB>) {
    // Initialize logger asynchronously
    getLogger().then((logger) => {
      this.logger = logger;
    });
  }

  /**
   * Handle database errors and convert to ServiceError
   */
  protected handleDatabaseError(error: {
    code?: string;
    message?: string;
  }): ServiceError {
    // PostgreSQL error codes
    // https://www.postgresql.org/docs/current/errcodes-appendix.html

    if (!error) {
      return new ServiceError('DATABASE_ERROR', 'Unknown database error');
    }

    // Observability side-effect: "always a bug" DB errors (missing GRANT,
    // schema drift) are otherwise swallowed by the Result pattern and never
    // reach Sentry, even though the DB logs thousands of them. Capture here —
    // BEFORE the classification below — WITHOUT changing the ServiceError this
    // method returns. isAlwaysBugDbError excludes RLS write denials (42501 +
    // "violates row-level security policy"), which are expected authorization
    // outcomes, so this does not flood Sentry.
    if (isAlwaysBugDbError(error)) {
      captureServiceError(
        { message: error.message ?? '', code: error.code, details: error },
        {
          action: `db.${error.code}`,
          table: extractDeniedObjectName(error.message),
          sqlstate: error.code,
        },
      );
    }

    // Duplicate key violation
    if (error.code === '23505') {
      return new ServiceError(
        'DUPLICATE_ENTRY',
        extractDuplicateFieldFromError(error),
        error,
      );
    }

    // Foreign key violation
    if (error.code === '23503') {
      return new ServiceError(
        'FOREIGN_KEY_VIOLATION',
        'Referenced record does not exist',
        error,
      );
    }

    // Check constraint violation
    if (error.code === '23514') {
      return new ServiceError(
        'VALIDATION_ERROR',
        extractCheckConstraintFromError(error),
        error,
      );
    }

    // Not null violation
    if (error.code === '23502') {
      // PostgreSQL error message format: "null value in column \"column_name\" of relation..."
      // Try to extract the column name from the message
      const columnMatch = error.message?.match(/column "([^"]+)"/);
      const column =
        (error as { column?: string }).column || columnMatch?.[1] || 'unknown';
      return new ServiceError(
        'MISSING_REQUIRED_FIELD',
        `Missing required field: ${column}`,
        error,
      );
    }

    // Supabase/PostgREST specific errors
    if (error.code === 'PGRST116') {
      return new ServiceError('NOT_FOUND', 'Record not found', error);
    }

    // RLS policy violation
    if (
      error.code === '42501' ||
      error.message?.includes('row-level security')
    ) {
      return new ServiceError(
        'FORBIDDEN',
        'Access denied by security policy',
        error,
      );
    }

    // Check if error message contains HTML (indicates server error page instead of JSON)
    const rawMessage = error.message || '';
    const isHtmlResponse =
      rawMessage.includes('<!DOCTYPE') || rawMessage.includes('<html');

    if (isHtmlResponse) {
      // Transient infrastructure outage — use a distinct code so callers and
      // Sentry filters can treat it separately from real DB errors.
      return new ServiceError(
        'SERVICE_UNAVAILABLE',
        'Database service temporarily unavailable',
        error,
      );
    }

    // "terminated" means the underlying fetch/HTTP connection was aborted
    // (e.g. Vercel function timeout, network interruption). Treat as transient
    // so Sentry does not file a code-bug issue for it.
    if (rawMessage === 'terminated' || rawMessage.includes('terminated')) {
      return new ServiceError(
        'SERVICE_UNAVAILABLE',
        'Database connection terminated (possible timeout)',
        error,
      );
    }

    // PostgREST emits "Could not query the database for the schema cache.
    // Retrying." during transient schema reloads. The client auto-retries —
    // capturing this as a code bug pages on-call for a non-incident.
    // Source: BALLEE-CG / BALLEE-CH / BALLEE-CJ (2026-05-16, ~3 events in 2min
    // across automation-triggers + automation-processor crons).
    if (/schema cache.*retrying/i.test(rawMessage)) {
      return new ServiceError(
        'SERVICE_UNAVAILABLE',
        'PostgREST schema cache reload in progress (transient)',
        error,
      );
    }

    const message = rawMessage || 'Database operation failed';

    // Default database error
    return new ServiceError('DATABASE_ERROR', message, error);
  }

  /**
   * Validate UUID format
   */
  protected validateUUID(id: string, fieldName = 'id'): Result<void> {
    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

    if (!uuidRegex.test(id)) {
      return Result.fail(
        new ServiceError(
          'VALIDATION_ERROR',
          `Invalid UUID format for ${fieldName}`,
        ),
      );
    }

    return Result.ok(undefined);
  }

  /**
   * Validate required fields
   */
  protected validateRequired<T>(
    value: T | null | undefined,
    fieldName: string,
  ): Result<T> {
    if (value === null || value === undefined || value === '') {
      return Result.fail(
        new ServiceError(
          'MISSING_REQUIRED_FIELD',
          `Missing required field: ${fieldName}`,
        ),
      );
    }

    return Result.ok(value);
  }

  /**
   * Validate date range
   */
  protected validateDateRange(
    startDate: Date | string,
    endDate: Date | string,
  ): Result<void> {
    const start = new Date(startDate);
    const end = new Date(endDate);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return Result.fail(
        new ServiceError('VALIDATION_ERROR', 'Invalid date format'),
      );
    }

    if (start > end) {
      return Result.fail(
        new ServiceError(
          'VALIDATION_ERROR',
          'Start date must be before or equal to end date',
        ),
      );
    }

    return Result.ok(undefined);
  }

  /**
   * Execute operation with error handling
   */
  protected async execute<T>(
    operation: () => Promise<T>,
    errorMessage = 'Operation failed',
  ): Promise<Result<T>> {
    try {
      const result = await operation();
      return Result.ok(result);
    } catch (error: unknown) {
      // Only capture unexpected errors to Sentry — skip expected business logic errors
      // (validation, duplicate entries, not found, forbidden) to reduce noise
      const isExpectedError =
        error instanceof ServiceError &&
        [
          'VALIDATION_ERROR',
          'DUPLICATE_ENTRY',
          'NOT_FOUND',
          'FORBIDDEN',
          'MISSING_REQUIRED_FIELD',
          // Transient infrastructure unavailability — not a code bug, suppress Sentry noise
          'SERVICE_UNAVAILABLE',
          // Idempotency guards on automation enrollment: thrown by
          // EnrollmentService.enrollUser when a user is already in a given
          // state, and treated as a silent skip by TriggerService.processTrigger.
          // The 5-min automation-triggers cron re-attempts enrollment of
          // already-enrolled users every run, so capturing these floods Sentry
          // with non-actionable noise (BALLEE-DZ: 1087 events in 95 min).
          'ENROLLMENT_ACTIVE',
          'ENROLLMENT_COMPLETED',
          'ENROLLMENT_EXITED',
          // Same class: enrollUser refusing a placeholder account whose
          // address can never receive mail (BALLEE-K6). Re-offered by the
          // cron on every run, so capturing it would page on every run.
          'RECIPIENT_UNDELIVERABLE',
        ].includes(error.code);

      if (!isExpectedError) {
        captureServerActionError(error, {
          action: 'BaseService.execute',
          step: 'operation-execution',
        });
      }

      if (error instanceof ServiceError) {
        return Result.fail(error);
      }
      return Result.fail(
        new ServiceError('INTERNAL_ERROR', errorMessage, error),
      );
    }
  }

  /**
   * Check if record exists
   */
  protected async recordExists(
    table: string,
    id: string,
  ): Promise<Result<boolean>> {
    try {
      // Cast to untyped client for dynamic table name access
      const { data, error } = await (this.client as SupabaseClient)
        .from(table)
        .select('id')
        .eq('id', id)
        .single();

      if (error && error.code !== 'PGRST116') {
        return Result.fail(this.handleDatabaseError(error));
      }

      return Result.ok(!!data);
    } catch (error: unknown) {
      return Result.fail(
        new ServiceError(
          'DATABASE_ERROR',
          `Failed to check if ${table} exists`,
          error,
        ),
      );
    }
  }

  /**
   * Log operation using proper logger
   */
  protected async log(
    level: 'info' | 'warn' | 'error' | 'debug',
    message: string,
    context?: Record<string, unknown>,
  ) {
    if (!this.logger) {
      // Logger not yet initialized, skip logging
      return;
    }

    if (context) {
      this.logger[level](context, message);
    } else {
      this.logger[level](message);
    }
  }

  /**
   * Log audit action to audit_logs table
   *
   * Centralizes audit logging for all services. Non-blocking - failures
   * are logged but don't interrupt the main operation.
   *
   * @example
   * ```typescript
   * await this.logAction({
   *   action: AuditAction.PROFILE_UPDATE,
   *   resourceType: 'profile',
   *   resourceId: profileId,
   *   accountId: workspace.account.id,
   *   metadata: { fieldsUpdated: ['name', 'email'] },
   * });
   * ```
   */
  protected async logAction(params: {
    action: AuditAction | string;
    resourceType: AuditResourceType | string;
    resourceId?: string;
    accountId?: string;
    metadata?: Record<string, unknown>;
    changes?: {
      before?: Record<string, unknown>;
      after?: Record<string, unknown>;
    };
  }): Promise<void> {
    try {
      // Get authenticated user
      const {
        data: { user },
      } = await this.client.auth.getUser();

      if (!user) {
        this.log('warn', 'Audit logging skipped - no authenticated user');
        return;
      }

      // Insert audit log entry
      // Cast to untyped client since TDB is generic and TypeScript can't resolve the insert type
      const { error } = await (this.client as SupabaseClient)
        .from('audit_logs')
        .insert({
          actor_id: user.id, // Who performed the action
          user_email: user.email || '',
          action: params.action,
          resource_type: params.resourceType,
          resource_id: params.resourceId,
          account_id: params.accountId,
          metadata: params.metadata,
          changes: params.changes,
        });

      if (error) {
        this.log('warn', 'Audit logging failed', {
          error,
          action: params.action,
          resourceType: params.resourceType,
        });
      }
    } catch (error) {
      this.log('warn', 'Audit logging failed', {
        error,
        action: params.action,
      });
    }
  }

  /**
   * Log multiple audit actions in a batch
   *
   * More efficient than multiple logAction calls when logging
   * multiple related actions (e.g., bulk updates).
   *
   * @example
   * ```typescript
   * await this.logBatch([
   *   { action: AuditAction.CAST_ASSIGN, resourceType: 'cast_assignment', resourceId: id1 },
   *   { action: AuditAction.CAST_ASSIGN, resourceType: 'cast_assignment', resourceId: id2 },
   * ]);
   * ```
   */
  protected async logBatch(
    entries: Array<{
      action: AuditAction | string;
      resourceType: AuditResourceType | string;
      resourceId?: string;
      accountId?: string;
      metadata?: Record<string, unknown>;
      changes?: {
        before?: Record<string, unknown>;
        after?: Record<string, unknown>;
      };
    }>,
  ): Promise<void> {
    if (entries.length === 0) return;

    try {
      // Get authenticated user
      const {
        data: { user },
      } = await this.client.auth.getUser();

      if (!user) {
        this.log('warn', 'Batch audit logging skipped - no authenticated user');
        return;
      }

      // Map entries to audit log records
      const auditLogs = entries.map((entry) => ({
        actor_id: user.id,
        user_email: user.email || '',
        action: entry.action,
        resource_type: entry.resourceType,
        resource_id: entry.resourceId,
        account_id: entry.accountId,
        metadata: entry.metadata,
        changes: entry.changes,
      }));

      // Insert all at once
      // Cast to untyped client since TDB is generic and TypeScript can't resolve the insert type
      const { error } = await (this.client as SupabaseClient)
        .from('audit_logs')
        .insert(auditLogs);

      if (error) {
        this.log('warn', 'Batch audit logging failed', {
          error,
          entriesCount: entries.length,
        });
      }
    } catch (error) {
      this.log('warn', 'Batch audit logging failed', {
        error,
        entriesCount: entries.length,
      });
    }
  }

  /**
   * Execute a Supabase query with standardized error handling and logging.
   *
   * Reduces boilerplate for service methods by:
   * - Logging start and completion
   * - Handling Supabase { data, error } responses
   * - Converting database errors to ServiceError
   * - Capturing errors in Sentry
   *
   * @example
   * ```typescript
   * // Before: ~20 lines per method
   * async getVenueById(id: string): Promise<Result<Venue>> {
   *   const context = { venueId: id, operation: 'getVenueById' };
   *   try {
   *     this.log('info', 'Getting venue', context);
   *     const { data, error } = await this.client.from('venues')...
   *     if (error) { this.log('error'...); return Result.fail(...); }
   *     this.log('info', 'Got venue', context);
   *     return Result.ok(data);
   *   } catch (error) {
   *     captureServiceError(...);
   *     return Result.fail(...);
   *   }
   * }
   *
   * // After: 5 lines per method
   * async getVenueById(id: string): Promise<Result<Venue>> {
   *   return this.executeQuery({
   *     method: 'getVenueById',
   *     context: { venueId: id },
   *     query: () => this.client.from('venues').select('*').eq('id', id).single(),
   *   });
   * }
   * ```
   *
   * @param options - Query configuration
   * @param options.method - Method name for logging (e.g., 'getVenueById')
   * @param options.context - Additional context for logging (e.g., { venueId: '...' })
   * @param options.query - Async function that returns Supabase query builder (will be awaited)
   * @param options.errorMessage - Custom error message (defaults to 'Operation failed')
   * @param options.silent - Skip logging on success (useful for frequent operations)
   * @returns Result<T> with data on success or ServiceError on failure
   */
  protected async executeQuery<T>({
    method,
    context = {},
    query,
    errorMessage,
    silent = false,
  }: {
    method: string;
    context?: Record<string, unknown>;
    query: () => Promise<{
      data: T | null;
      error: { code?: string; message?: string } | null;
    }>;
    errorMessage?: string;
    silent?: boolean;
  }): Promise<Result<T>> {
    const fullContext = { ...context, operation: method };

    try {
      if (!silent) {
        this.log('info', `Starting ${method}`, fullContext);
      }

      const { data, error } = await query();

      if (error) {
        this.log('error', `${method} failed`, { ...fullContext, error });
        return Result.fail(this.handleDatabaseError(error));
      }

      if (data === null) {
        return Result.fail(
          new ServiceError('NOT_FOUND', errorMessage || 'Record not found'),
        );
      }

      if (!silent) {
        this.log('info', `${method} completed`, fullContext);
      }

      return Result.ok(data);
    } catch (error: unknown) {
      captureServerActionError(error, {
        action: method,
        step: 'database-query',
        ...context,
      });

      this.log('error', `Unexpected error in ${method}`, {
        ...fullContext,
        error,
      });

      return Result.fail(
        new ServiceError(
          'INTERNAL_ERROR',
          errorMessage || `${method} failed unexpectedly`,
          error,
        ),
      );
    }
  }

  /**
   * Execute a Supabase query that returns an array (may be empty).
   *
   * Similar to executeQuery but handles array results where empty is valid.
   *
   * @example
   * ```typescript
   * async getVenuesByCity(city: string): Promise<Result<Venue[]>> {
   *   return this.executeQueryArray({
   *     method: 'getVenuesByCity',
   *     context: { city },
   *     query: () => this.client.from('venues').select('*').eq('city', city),
   *   });
   * }
   * ```
   */
  protected async executeQueryArray<T>({
    method,
    context = {},
    query,
    errorMessage,
    silent = false,
  }: {
    method: string;
    context?: Record<string, unknown>;
    query: () => Promise<{
      data: T[] | null;
      error: { code?: string; message?: string } | null;
    }>;
    errorMessage?: string;
    silent?: boolean;
  }): Promise<Result<T[]>> {
    const fullContext = { ...context, operation: method };

    try {
      if (!silent) {
        this.log('info', `Starting ${method}`, fullContext);
      }

      const { data, error } = await query();

      if (error) {
        this.log('error', `${method} failed`, { ...fullContext, error });
        return Result.fail(this.handleDatabaseError(error));
      }

      if (!silent) {
        this.log('info', `${method} completed`, {
          ...fullContext,
          count: data?.length ?? 0,
        });
      }

      return Result.ok(data ?? []);
    } catch (error: unknown) {
      captureServerActionError(error, {
        action: method,
        step: 'database-query',
        ...context,
      });

      this.log('error', `Unexpected error in ${method}`, {
        ...fullContext,
        error,
      });

      return Result.fail(
        new ServiceError(
          'INTERNAL_ERROR',
          errorMessage || `${method} failed unexpectedly`,
          error,
        ),
      );
    }
  }

  /**
   * Check if a record has related records in another table.
   *
   * Useful for dependency checks before deletion.
   * Uses count with head:true for efficiency (doesn't fetch data).
   *
   * @example
   * ```typescript
   * // Before deleting a client, check for related records
   * const hasEvents = await this.hasRelatedRecords('events', 'client_id', clientId);
   * const hasProductions = await this.hasRelatedRecords('production_clients', 'client_id', clientId);
   *
   * if (hasEvents || hasProductions) {
   *   return Result.fail(new ServiceError('HAS_DEPENDENCIES', 'Client has related records'));
   * }
   * ```
   *
   * @param table - Table to check for related records
   * @param column - Column name containing the foreign key
   * @param value - Value to match against the column
   * @returns true if related records exist, false otherwise
   */
  protected async hasRelatedRecords(
    table: string,
    column: string,
    value: string,
  ): Promise<boolean> {
    try {
      // Cast to untyped client for dynamic table name access
      const { count } = await (this.client as SupabaseClient)
        .from(table)
        .select('*', { count: 'exact', head: true })
        .eq(column, value);

      return (count ?? 0) > 0;
    } catch (error) {
      this.log('warn', 'Error checking related records', {
        table,
        column,
        value,
        error,
      });
      // Return true on error to be safe (prevents accidental deletion)
      return true;
    }
  }

  /**
   * Count related records in a table.
   *
   * Similar to hasRelatedRecords but returns the actual count.
   *
   * @example
   * ```typescript
   * const eventCount = await this.countRelatedRecords('events', 'client_id', clientId);
   * console.log(`Client has ${eventCount} events`);
   * ```
   */
  protected async countRelatedRecords(
    table: string,
    column: string,
    value: string,
  ): Promise<number> {
    try {
      // Cast to untyped client for dynamic table name access
      const { count } = await (this.client as SupabaseClient)
        .from(table)
        .select('*', { count: 'exact', head: true })
        .eq(column, value);

      return count ?? 0;
    } catch (error) {
      this.log('warn', 'Error counting related records', {
        table,
        column,
        value,
        error,
      });
      return 0;
    }
  }
}
